import { useCallback, useEffect, useState } from 'react';

const isNotificationSupported = () => typeof window !== 'undefined' && 'Notification' in window;

const getCurrentPermission = () => (isNotificationSupported() ? Notification.permission : 'unsupported');

/**
 * Thin wrapper over the browser Notification API.
 * permission: 'default' | 'granted' | 'denied' | 'unsupported'
 */
export const useBrowserNotification = () => {
    const [permission, setPermission] = useState(getCurrentPermission);

    // Keep in sync if the user changes the permission from the browser's site settings
    useEffect(() => {
        if (!isNotificationSupported() || !navigator.permissions?.query) return undefined;

        let permissionStatus;
        const handleChange = () => setPermission(getCurrentPermission());

        navigator.permissions.query({ name: 'notifications' })
            .then((status) => {
                permissionStatus = status;
                permissionStatus.addEventListener('change', handleChange);
            })
            .catch(() => {
                // Permissions API doesn't know 'notifications' in some browsers — nothing to sync
            });

        return () => permissionStatus?.removeEventListener('change', handleChange);
    }, []);

    // Browsers only show the permission prompt from a user gesture, so call this from a click handler
    const requestPermission = useCallback(async () => {
        if (!isNotificationSupported()) return 'unsupported';
        if (Notification.permission !== 'default') {
            setPermission(Notification.permission);
            return Notification.permission;
        }

        try {
            const result = await Notification.requestPermission();
            setPermission(result);
            return result;
        } catch {
            return getCurrentPermission();
        }
    }, []);

    const showNotification = useCallback(({ title, body, icon, image, tag, onClick } = {}) => {
        if (!isNotificationSupported() || Notification.permission !== 'granted') return null;

        try {
            const notification = new Notification(title || 'New notification', {
                body,
                icon: icon || '/favicon.svg',
                image,
                // Same tag across tabs makes the browser replace instead of stacking duplicates
                tag,
            });

            notification.onclick = (event) => {
                event.preventDefault();
                window.focus();
                onClick?.();
                notification.close();
            };

            return notification;
        } catch {
            // Some mobile browsers only allow notifications through a service worker
            return null;
        }
    }, []);

    return { permission, isSupported: permission !== 'unsupported', requestPermission, showNotification };
};
